import {z} from "zod";
import {useBearerToken} from "../auth/use-bearer-token";
import {B2Occupancy, Cart, createApiClient} from "./client";

export const useSiteApi = () => {
	const accessToken = useBearerToken();
	const api = createApiClient({accessToken});
	return {
		listSites: async (tenantId: string) =>
			api.get("/api/booking/v2/tenants/{tenantId}/sites", {
				path: {tenantId},
				query: {
					size: 999,
					sort: ["activeFrom", "name"],
				},
			}),
		getSite: async (tenantId: string, siteId: string) =>
			api.get("/api/booking/v2/tenants/{tenantId}/sites/{siteId}", {
				path: {tenantId, siteId},
			}),
		getAvailability: async (tenantId: string, siteId: string, query: OrderDatesRequest) =>
			api.get("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/availability", {
				path: {tenantId, siteId},
				query,
			}),
		getPricing: async (tenantId: string, siteId: string, query: OrderDatesRequest) =>
			api.get("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/pricing", {
				path: {tenantId, siteId},
				query,
			}),
		checkDiscount: async (tenantId: string, siteId: string, discountCode: string) =>
			api.get("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/discounts/{discountCode}", {
				path: {tenantId, siteId, discountCode},
			}),
		submitOrder: async (tenantId: string, siteId: string, body: OrderRequest) =>
			api.post("/api/booking/v2/tenants/{tenantId}/sites/{siteId}/bookings", {
				path: {tenantId, siteId},
				body: {
					...body,
					boxes: body.boxes,
				},
			}),
		getBooking: async (bookingId: string) =>
			api.get("/api/booking/v2/bookings/{bookingId}", {
				path: {bookingId},
			}),
		cancelBooking: async (bookingId: string) =>
			api.delete("/api/booking/v2/bookings/{bookingId}", {
				path: {bookingId},
			}),
		prolongBooking: async (bookingId: string, body: BookingProlongRequest) =>
			api.post("/api/booking/v2/bookings/{bookingId}/prolong", {
				path: {bookingId},
				body,
			}),
		verifyBookingLocation: async (bookingId: string, cubeId: string) =>
			await api.post("/api/booking/v2/bookings/{bookingId}/cube-verification", {
				path: {bookingId},
				body: {cubeId},
			}) as Promise<BoxAvailability>,
		occupyBoxes: async (bookingId: string, cubeId: string) =>
			api.post("/api/booking/v2/bookings/{bookingId}/occupy", {
				path: {bookingId},
				body: {cubeId},
			}),
		openBox: async (bookingId: string, body: B2Occupancy) =>
			api.post("/api/booking/v2/bookings/{bookingId}/open-box", {
				path: {bookingId},
				body,
			}),
		changeBox: async (bookingId: string, body: B2Occupancy) =>
			api.post("/api/booking/v2/bookings/{bookingId}/change-box", {
				path: {bookingId},
				body,
			}),
		openBoxes: async (bookingId: string) =>
			api.post("/api/booking/v2/bookings/{bookingId}/open-boxes", {
				path: {bookingId},
			}),
		releaseLockers: async (bookingId: string) =>
			api.post("/api/booking/v2/bookings/{bookingId}/release-lockers", {
				path: {bookingId},
			}),
		openAdmission: async (siteId: string, bookingId?: string) =>
			api.post("/api/booking/v2/sites/{siteId}/admission/open", {
				path: {siteId},
				header: bookingId ? {"X-Booking-Id": bookingId} : {},
			}),
		getMyBookings: async (params?: { siteId?: string; activeAt?: string }) =>
			api.get("/api/booking/v2/bookings", {query: params ?? {}}),
		resolveCube: async (cubeId: string) =>
			api.get("/api/booking/v2/cubes/{cubeId}", {
				path: {cubeId},
			}),
		getCubeAvailabilities: async (bookingId: string): Promise<CubeAvailability[]> => {
			// The generated client types `availability` as `z.unknown()` (that's how the map type is emitted), so
			// parse the response with our local schema instead of a blind cast to get real runtime validation.
			const response = await api.get("/api/booking/v2/bookings/{bookingId}/cube-availabilities", {
				path: {bookingId},
			});
			return z.array(CubeAvailability).parse(response);
		},
	};
};

type OrderDatesRequest = {
	from?: string;
	until?: string;
	fromDateTime?: string;
	hours?: number;
};

export type OrderRequest = Cart & {
	boxes: Record<string, number>;
	locale: string;
};

type BookingProlongRequest = {
	until?: string;
	hours?: number;
	discountCode?: string;
};

export type BoxPackagePrice = z.infer<typeof BoxPackagePrice>;
const BoxPackagePrice = z.record(z.number(), z.number());

export type BoxUnitPrice = z.infer<typeof BoxUnitPrice>;
export const BoxUnitPrice = z.record(z.number(), z.number());

export type BoxAvailability = z.infer<typeof BoxAvailability>;
export const BoxAvailability = z.record(z.string(), z.number());

/** Live box availability of a single cube at the booking's site (mirrors backend B2CubeAvailability). */
export type CubeAvailability = z.infer<typeof CubeAvailability>;
export const CubeAvailability = z.object({
	cubeId: z.string(),
	cubeDescription: z.string().optional(),
	availability: BoxAvailability,
});

export type BoxPricing = z.infer<typeof BoxPricing>;
const BoxPricing = z.record(z.string(), z.number());

/**
 * Stable error codes emitted by the admission endpoints in the {@code code} property of the RFC-7807 ProblemDetail
 * body. Mirrors AdmissionErrorCode on the backend.
 */
export type AdmissionErrorCode = "BOOKING_REQUIRED" | "OUTSIDE_HOURS" | "RATE_LIMITED";
